import { SmartSendProvider } from '~/lib/consts/providers';
import {
  InputRuleApiModel,
  Customer,
  SmartSendItemApiModel,
  Validation,
  ValidationType,
  Product,
  Dimensions
} from '~/lib/api/smartsend/types';
import { FindCountry, FindCountryName } from '../cache/CountryCache';
import { EoriNumber, IossNumber, TaxId, VatNumber, VatStatus } from '../consts/ItemMetaConstants';
import { Individual, Registered } from '../consts/TaxStatusConsts';
import { onlyUnique, SumNumber } from './ArrayHelper';
import { IsInvariantStringMatch } from './IsMatch';
import { DeliveredDutyPaidExtra } from '~/lib/consts/extras';
import { IsTariffCodeTooShort } from '../../components/customs/GetTariffCodeFromSuggestion';
import GetTariffSystemFromItem from './GetTariffSystemFromItem';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { isDeliveryToShopQuote } from './IsDeliveryToShopQuote';
import conditionalIsRequired from './CustomerConditionalHelper';
import { EmailPattern } from './RuleValidation';
import { MapConsignmentContentToProduct } from '../../components/smartsend/grouping/MapMarketplaceOrderlineToConsignmentParcel';

export const CustomerVatFields = [EoriNumber, VatStatus, VatNumber, TaxId];

/**
 * Both validators build a fresh result rather than writing into the object they were handed.
 *
 * They used to mutate the caller's Validation, which meant passing an item's LIVE validation state
 * silently corrupted it - a component doing so pushed errors onto the item on every render. Callers
 * worked around it by constructing a throwaway object; now they do not have to. Every existing
 * caller already uses the return value, so nothing depended on the mutation.
 */
const startValidation = (validation?: Validation): Validation =>
  validation
    ? { ...validation, Errors: [...(validation.Errors ?? [])], Warnings: [...(validation.Warnings ?? [])] }
    : { Validated: false, Errors: [], Warnings: [] };

export default function PreQuoteValidation(item: SmartSendItemApiModel, validation?: Validation): Validation {
  const output = startValidation(validation);

  const validateConsignment = item.Consignment?.Parcels?.length > 0;

  if (validateConsignment) {
    if (item.Consignment.Parcels.some((p) => isZeroOrNegative(p.Dimensions?.Weight))) {
      output.Errors.push('ITEM-WEIGHT');
    }

    // A parcel with nothing declared in it must never reach the API. QuoteHelper.MapQuoteRequest
    // prices it, OrderMapper.MapParcels then drops it - so the customer is quoted for N parcels and
    // shipped N-1, silently. Worse, if EVERY parcel is empty MapParcels falls through to its
    // no-consignment branch and books an entirely different single-parcel shipment from
    // PackageDimensions and MarketplaceOrderLines. The loop below cannot catch this: zero rows
    // produce zero errors.
    if (item.Consignment.Parcels.some((p) => !(p.Contents?.length > 0))) {
      output.Errors.push('ITEM-PARCEL-EMPTY');
    }

    // `?? []` is load-bearing, not defensive noise: flatMap over a parcel whose Contents is undefined
    // yields [undefined] rather than [], so the callback below then threw
    // "Cannot read properties of undefined (reading 'Value')" and took the whole item list down.
    // An absent Contents is the same problem as an empty one and is reported as ITEM-PARCEL-EMPTY above.
    item.Consignment.Parcels.flatMap((p) => p.Contents ?? []).forEach((o) => {
      if (o.Value <= 0) {
        output.Errors.push('ITEM-VALUE');
      }

      if (!o.Description) {
        output.Errors.push('ITEM-DESCRIPTION');
      }
    });
  } else {
    if (!item.PackageDimensions?.Weight) {
      output.Errors.push('ITEM-WEIGHT');
    }

    item.MarketplaceOrderLines?.forEach((o) => {
      if (o.Value <= 0) {
        output.Errors.push('ITEM-VALUE');
      }

      if (!o.Product?.Description) {
        output.Errors.push('ITEM-DESCRIPTION');
      }
    });
  }

  if (!item.Service?.Id) {
    output.Errors.push('SERVICE-MISSING');
  }

  if (!item.Recipient?.CountryIsoCode) {
    output.Errors.push('ADDRESS-RECIPIENT');
  }

  if (!item.Sender?.CountryIsoCode) {
    output.Errors.push('ADDRESS-SENDER');
  }

  if (item.Service?.Provider === SmartSendProvider && item.Sender?.Postcode === item.Recipient?.Postcode) {
    output.Errors.push('ADDRESS-RECIPIENT-SAMEPOSTCODE');
  }

  return output;
}

export function RequiresCustoms(validation: Validation): boolean {
  return validation?.QuoteState?.Tags?.includes('Customs') ?? false;
}

export function RequiresTaxIdsButNotCustoms(validation: Validation): boolean {
  return (
    (!validation?.QuoteState?.Tags?.includes('Customs') &&
      (validation?.QuoteState?.SenderValidation?.some((x) => CustomerVatFields.includes(x.name)) ||
        validation?.QuoteState?.RecipientValidation?.some((x) => CustomerVatFields.includes(x.name)))) ??
    false
  );
}

export function NoQuoteValidation(_: SmartSendItemApiModel, e: any): Validation {
  const output: Validation = {
    Validated: false,
    Errors: [],
    Warnings: []
  };

  if (e) {
    console.error(e);
  }

  output.Errors.push('SERVICE-UNAVAILABLE');

  return output;
}

const validateCustomerTaxError = (errors: string[], type: 'RECIPIENT' | 'SENDER') => {
  const taxErrors = [
    `CUSTOMS-${type}-VATNUMBER-CONDITIONALLY-REQUIRED`,
    `CUSTOMS-${type}-TAXID-CONDITIONALLY-REQUIRED`
  ];
  if (taxErrors.every((e) => errors.includes(e))) {
    errors.push(`CUSTOMS-${type}-VATNUMBER-OR-TAXID-REQUIRED`);
    return errors.filter((e) => taxErrors.includes(e) === false);
  }
  return errors;
};

/**
 * The tail every non-trivial exit from PostQuoteValidation shares: collapse the either/or tax errors,
 * dedupe, then decide whether the item validated.
 *
 * Extracted once the no-quote bail-out could carry real findings out with it - returning raw would emit
 * both halves of an either/or tax pair and repeat an error the caller already held.
 *
 * `Validated` is only ever set true here, never false, which is the pre-existing contract.
 */
const finishValidation = (output: Validation): Validation => {
  output.Errors = validateCustomerTaxError(output.Errors, 'RECIPIENT');
  output.Errors = validateCustomerTaxError(output.Errors, 'SENDER');
  output.Errors = output.Errors.filter(onlyUnique);

  if (output.Errors.length === 0) {
    output.Validated = true;
  }

  return output;
};

const DimsAreZero = ({ Weight, Length, Width, Height }: Dimensions) => {
  return isZeroOrNegative(Weight) || isZeroOrNegative(Length) || isZeroOrNegative(Width) || isZeroOrNegative(Height);
};

export function PostQuoteValidation(item: SmartSendItemApiModel, validation?: Validation): Validation {
  const output = startValidation(validation);

  const validateConsignment = item.Consignment?.Parcels?.length > 0;

  if (validateConsignment) {
    if (item.Consignment.Parcels.some((p) => isZeroOrNegative(p.Dimensions.Weight))) {
      output.Errors.push('ITEM-WEIGHT');
      return output;
    }
  } else {
    if (isZeroOrNegative(item.PackageDimensions?.Weight)) {
      output.Errors.push('ITEM-WEIGHT');
      return output;
    }
  }

  // Prohib
  validation?.QuoteState?.Restrictions?.forEach((r) => {
    switch (r.Restriction?.toUpperCase()) {
      case 'CONFIRM':
        output.Warnings.push(r.Error);
        break;
      case 'INVALID':
        output.Errors.push(`ITEM-PROHIB-${r.Restriction.toUpperCase()}`);
        output.Warnings.push(r.Error);
        break;
    }
  });

  // Runs BEFORE the no-quote bail-out below: quoting returns Recipient/SenderValidation whether or not it
  // could price the shipment, and a bad address is frequently WHY it could not, so bailing out on
  // SERVICE-UNAVAILABLE alone threw away the one message that said what to fix.
  //
  // Sound this early because nothing here reads the Quote - each block is gated on the rule set it
  // validates against, and the county checks need only the item's own country.

  // Customer - Non-Vat
  if (validation?.QuoteState?.RecipientValidation) {
    output.Errors = output.Errors.concat(
      addressHasIssues(
        'Recipient',
        'ADDRESS-RECIPIENT',
        item.Recipient,
        item.Sender,
        validation?.QuoteState?.RecipientValidation.filter((x) => !CustomerVatFields.includes(x.name))
      )
    );
  }

  if (validation?.QuoteState?.SenderValidation) {
    output.Errors = output.Errors.concat(
      addressHasIssues(
        'Sender',
        'ADDRESS-SENDER',
        item.Sender,
        item.Recipient,
        validation?.QuoteState?.SenderValidation.filter((x) => !CustomerVatFields.includes(x.name))
      )
    );
  }

  // Customer - Vat
  if (validation?.QuoteState?.RecipientValidation) {
    output.Errors = output.Errors.concat(
      addressHasIssues(
        'Recipient',
        'CUSTOMS-RECIPIENT',
        item.Recipient,
        item.Sender,
        validation?.QuoteState?.RecipientValidation.filter((x) => CustomerVatFields.includes(x.name))
      )
    );
  }

  if (validation?.QuoteState?.SenderValidation) {
    output.Errors = output.Errors.concat(
      addressHasIssues(
        'Sender',
        'CUSTOMS-SENDER',
        item.Sender,
        item.Recipient,
        validation?.QuoteState?.SenderValidation.filter((x) => CustomerVatFields.includes(x.name))
      )
    );
  }

  // Recipient County Validation
  const recipientStates = FindCountry(item.Recipient)?.States;
  if (recipientStates?.length > 0 && !recipientStates.some((x) => x.Code === item.Recipient.County)) {
    output.Errors.push('ADDRESS-RECIPIENT-COUNTY-INVALID-OPTION');
  }

  // Sender County Validation
  const senderStates = FindCountry(item.Sender)?.States;
  if (senderStates?.length > 0 && !senderStates.some((x) => x.Code === item.Sender.County)) {
    output.Errors.push('ADDRESS-SENDER-COUNTY-INVALID-OPTION');
  }

  // Anything found above is reported INSTEAD of the quote checks below, SERVICE-UNAVAILABLE included: the
  // row shows one headline, SERVICE-UNAVAILABLE outranks everything in ErrorCodeResolutions, and a
  // concrete field to fix beats a dead end. The quote is only judged once the addresses stand up.
  //
  // Both callers pass a fresh `Errors: []` (useQuoteHelper, ConsignmentSummary), so this list holds only
  // what this pass raised. Note the restrictions above land in it too, so a prohibited item also stops here.
  if (output.Errors.length > 0) {
    return finishValidation(output);
  }

  // Through the shared tail, so a bail-out is reported the way a full pass would be.
  if (!output.QuoteState || !output.QuoteState.Quote) {
    output.Errors.push('SERVICE-UNAVAILABLE');
    return finishValidation(output);
  }

  const marketplaceCourierLimits = SettingsCache.settings.MarketplaceCourierLimits;
  const itemMarketplaceIds = item.MarketplaceOrderLines.map((m) => {
    return m.Source.Id;
  }).filter(onlyUnique);

  itemMarketplaceIds.forEach((marketplaceId) => {
    const allowedCouriers = marketplaceCourierLimits[marketplaceId];
    if (allowedCouriers && allowedCouriers.size > 0) {
      if (!allowedCouriers.has(output.QuoteState.Quote.Service.CourierId)) {
        output.Errors.push(`SERVICE-UNSUPPORTED-MARKETPLACE-${marketplaceId.toUpperCase()}`);
      }
    }
  });

  const possibleQuoteExtras = [];
  output.QuoteState.Quote.Options.forEach((o) => {
    o.AvailableExtras.forEach((oa) => {
      possibleQuoteExtras.push(oa.Id);
    });
    o.Extras.forEach((oa) => {
      possibleQuoteExtras.push(oa.Id);
    });
  });

  if (item.Extras.filter((e) => !possibleQuoteExtras.includes(e.Id)).length > 0) {
    output.Errors.push('EXTRAS-UNAVAILABLE');
  }

  if (validation?.QuoteState?.ProductValidation) {
    if (validateConsignment) {
      // TODO (follow-up PR): a missing country of manufacture is INVENTED rather than rejected on the
      // multi-parcel path. OrderMapper.MapParcels maps `CountryOfManufacture ?? "GBR"` for consignment
      // contents, but leaves it blank on the single-parcel path - so the same missing field silently
      // becomes a GB origin declaration for one shipment shape and nothing for the other.
      //
      // Not fixed here on purpose. productHasIssues below already raises
      // PRODUCT-COUNTRYOFMANUFACTURE-REQUIRED wherever the service's ProductValidation marks the field
      // required, on BOTH paths, so the API default only bites on services that do not require it.
      // Making the UI demand it unconditionally would start blocking bookings that succeed today,
      // which is a commercial decision rather than a technical one. The likely correct fix is
      // dropping the `?? "GBR"` API-side so a bad declaration is rejected instead of fabricated.
      // Validate Consignment Parcels (Used for Order)
      // `?? []` guards the same undefined-Contents crash as PreQuoteValidation - see the note there.
      item.Consignment?.Parcels?.flatMap((p) => p.Contents ?? []).forEach((content) => {
        output.Errors = output.Errors.concat(
          productHasIssues(
            'PRODUCT',
            MapConsignmentContentToProduct(content, item.MarketplaceOrderLines),
            validation?.QuoteState?.ProductValidation,
            item.Sender,
            item.Recipient
          )
        );
      });
    } else {
      // Main Product Validation
      item.MarketplaceOrderLines.forEach((ol) => {
        output.Errors = output.Errors.concat(
          productHasIssues(
            'PRODUCT',
            ol.Product,
            validation?.QuoteState?.ProductValidation,
            item.Sender,
            item.Recipient
          )
        );
      });
    }
  }

  if (validation?.QuoteState?.ItemValidation) {
    output.Errors = output.Errors.concat(itemHasIssues('ITEM', item, validation?.QuoteState?.ItemValidation));
  }

  if (validation?.QuoteState?.Quote?.Service?.DimensionsRequired === true) {
    if (validateConsignment) {
      if (item.Consignment.Parcels.some((p) => DimsAreZero(p.Dimensions))) {
        output.Errors.push('ITEM-DIMENSIONS');
      }
    } else {
      if (DimsAreZero(item.PackageDimensions)) {
        output.Errors.push('ITEM-DIMENSIONS');
      }
    }
  }

  if (validation?.QuoteState?.Dates?.length > 0 && item.PreferredCollectionDate) {
    if (
      !validation?.QuoteState?.Dates.map((x) => new Date(x.Date).toDateString()).includes(
        new Date(item.PreferredCollectionDate).toDateString()
      )
    ) {
      output.Errors.push('SERVICE-COLLECTION-DATE');
    }
  }

  // Check the service supports multi-parcel shipments if the item has a consignment with multiple parcels
  if (validateConsignment && !SettingsCache.settings.Features.includes('Consignments')) {
    output.Errors.push('SERVICE-MULTIPARCEL-UNSUPPORTED');
  }

  // Non-DDU Services
  if (
    validation?.QuoteState?.Quote?.Service?.Tags.includes('dduUnavailable') ||
    validation?.QuoteState?.Quote?.Tags?.includes('dduUnavailable')
  ) {
    const canDoIoss = validation?.QuoteState?.ItemValidation?.some((x) => x.name === IossNumber);
    if (canDoIoss && item.Meta[IossNumber]?.Value) {
      // Thats great
    } else if (!item.Extras.find((e) => e.Id === DeliveredDutyPaidExtra)) {
      output.Errors.push('SERVICE-DDU-UNSUPPORTED');
    }
  }

  // Check Tariff Codes
  const tariffSystem = validation?.QuoteState?.Quote?.Service?.TariffSystem || GetTariffSystemFromItem(item);
  if (tariffSystem) {
    if (validateConsignment) {
      // Check Consignment Parcels for Tariff Codes (Used for Order)
      item.Consignment?.Parcels?.flatMap((p) => p.Contents ?? []).forEach((content) => {
        if (content.TariffCode) {
          if (IsTariffCodeTooShort(tariffSystem, content.TariffCode)) {
            output.Errors.push('PRODUCT-TARIFFCODE-TOO-SHORT');
          }
        }
      });
    } else {
      // Otherwise check Marketplace Order Lines for Tariff Codes
      item.MarketplaceOrderLines?.forEach((line) => {
        if (line.Product?.TariffCode) {
          if (IsTariffCodeTooShort(tariffSystem, line.Product.TariffCode)) {
            output.Errors.push('PRODUCT-TARIFFCODE-TOO-SHORT');
          }
        }
      });
    }
  }

  if (isDeliveryToShopQuote(validation?.QuoteState?.Quote)) {
    if (item.Recipient.Email && item.Sender.Email && item.Recipient.Email === item.Sender.Email) {
      if (validation?.QuoteState?.Quote?.Service?.DeliveryType === 'Locker') {
        output.Errors.push('ADDRESS-RECIPIENT-SAMEEMAIL-LOCKER-DELIVERY');
      } else {
        output.Errors.push('ADDRESS-RECIPIENT-SAMEEMAIL-SHOP-DELIVERY');
      }
    }
  }

  return finishValidation(output);
}

export function SmartSendFieldsAreValid(item: SmartSendItemApiModel): string[] {
  const errors = [];

  if (item.MarketplaceOrderLines) {
    item.MarketplaceOrderLines.forEach((x) => {
      const sku = x.Product?.Sku || '';
      if (sku.length > 50) {
        errors.push('SKU');
      }
    });
  }

  return errors.filter(onlyUnique);
}

export function SmartSendCreateFormValidation(item: SmartSendItemApiModel): string[] {
  const errors = SmartSendFieldsAreValid(item);

  if (!item.Description || item.Description.length > 256) {
    errors.push('DESC');
  }

  if (!item.Recipient?.Name || item.Recipient.Name.length > 50) {
    errors.push('RECIPIENT-NAME');
  }

  if (!item.Recipient?.Town || item.Recipient.Town.length > 50) {
    errors.push('RECIPIENT-TOWN');
  }

  const value = SumNumber(item.MarketplaceOrderLines?.map((x) => x.Value) || []) || item.Value;
  if (!value || value > 25000) {
    errors.push('VALUE');
  }

  if (!item.PackageDimensions?.Weight || item.PackageDimensions.Weight > 2500) {
    errors.push('WEIGHT');
  }

  if (
    !item.PackageDimensions?.Height ||
    item.PackageDimensions?.Height > 300 ||
    !item.PackageDimensions?.Width ||
    item.PackageDimensions?.Width > 300 ||
    !item.PackageDimensions?.Length ||
    item.PackageDimensions?.Length > 300
  ) {
    errors.push('DIMS');
  }

  return errors.filter(onlyUnique);
}

function isZero(v: any) {
  return v === 0 || v === '0' || !v;
}

function isZeroOrNegative(v: any) {
  return isZero(v) || v < 0;
}

function addressHasIssues(
  customerType: 'Sender' | 'Recipient',
  prefix: string,
  address: Customer,
  alternativeAddress: Customer,
  validation: InputRuleApiModel[]
): ValidationType[] {
  const issues = [];
  validation.forEach((r) => {
    const val = address[r.name] || address.Meta[r.name]?.Value;

    // Specific known named TaxIds
    const issueName = getNameToUseForAddressIssues(r);

    // Validation not required if individual
    if (r.name === EoriNumber && address.VatStatus === Individual) {
      r.required = false;
    }

    // VAT Number Required only when Registered
    if (r.name === VatNumber && address.VatStatus === Registered) {
      r.required = true;
    }

    if (val) {
      try {
        if (r.type === 'email') {
          // Relaxed validation for email addresses.
          if (!EmailPattern.test(val)) {
            issues.push(`${prefix}-${issueName}-FORMAT`);
          }
        } else if (r.pattern && !RegExp(r.pattern).test(val)) {
          issues.push(`${prefix}-${issueName}-FORMAT`);
        }
      } catch (e) {
        console.log(e, 'regex issue with', r.pattern, r.name);
      }

      if (r.minLength != undefined && r.minLength != null && r.minLength > val?.length) {
        issues.push(`${prefix}-${issueName}-MIN-LENGTH`);
      }

      if (r.maxLength != undefined && r.maxLength != null && r.maxLength < val?.length) {
        issues.push(`${prefix}-${issueName}-MAX-LENGTH`);
      }
    } else {
      if (r.required) {
        issues.push(`${prefix}-${r.name.toUpperCase()}-REQUIRED`);
      } else if (conditionalIsRequired(r, customerType, address, alternativeAddress)) {
        // Only Do Conditionally Required for TaxId and VatNumber.
        if ([TaxId, VatNumber].includes(r.name)) {
          issues.push(`${prefix}-${issueName}-CONDITIONALLY-REQUIRED`);
        } else {
          issues.push(`${prefix}-${issueName}-REQUIRED`);
        }
      }
    }
  });

  return issues;
}

function productHasIssues(
  prefix: string,
  product: Product,
  validation: InputRuleApiModel[],
  sender: Customer,
  recipient: Customer
): ValidationType[] {
  const issues = [];
  validation.forEach((r) => {
    // Meta is optional-chained: a marketplace order line's Product routinely arrives without it, and this
    // threw rather than reporting the missing field.
    const val = product[r.name] || product.Meta?.[r.name]?.Value;

    if (val) {
      try {
        if (r.pattern && !RegExp(r.pattern).test(val)) {
          issues.push(`${prefix}-${r.name.toUpperCase()}-FORMAT`);
        }
      } catch (e) {
        console.log(e);
      }
    } else {
      if (r.required || conditionalIsRequired(r, 'Sender', sender, recipient)) {
        issues.push(`${prefix}-${r.name.toUpperCase()}-REQUIRED`);
      }
    }

    if (r.required && r.name === 'CountryOfManufacture') {
      if (!IsInvariantStringMatch(FindCountryName(val), val)) {
        issues.push(`${prefix}-${r.name.toUpperCase()}-REQUIRED`);
      }
    }
  });
  return issues;
}

function itemHasIssues(prefix: string, item: SmartSendItemApiModel, validation: InputRuleApiModel[]): ValidationType[] {
  const issues = [];
  validation.forEach((r) => {
    const val = item[r.name] || item.Meta[r.name]?.Value;

    if (r.required && !val) {
      issues.push(`${prefix}-${r.name.toUpperCase()}-REQUIRED`);
    }

    if (val) {
      try {
        if (r.pattern && !RegExp(r.pattern).test(val)) {
          issues.push(`${prefix}-${r.name.toUpperCase()}-FORMAT`);
        }
      } catch (e) {
        console.log(e);
      }
    }
  });
  return issues;
}

function getNameToUseForAddressIssues(r: InputRuleApiModel) {
  return r.name === TaxId && r.label?.indexOf('UKIMS') > -1 ? 'UKIMS' : r.name.toUpperCase();
}
